import { useCallback, useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useForm } from "react-hook-form";
import { createSMTP } from "../../../../../api/EmailAPI";
import { notifications } from "@mantine/notifications";
import useNavigationBlocker from "../../../../../components/View/NavigationBlocker";
import {
  Accordion,
  Anchor,
  Button,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  PasswordInput,
  Stack,
  Switch,
  TagsInput,
  Text,
  TextInput,
  Title,
  SegmentedControl,
  Box,
} from "@mantine/core";
import BackButton from "../../../../../components/View/BackButton";
import {
  IconDeviceFloppy,
  IconPlus,
  IconSend,
  IconSignature,
  IconRefreshDot,
} from "@tabler/icons-react";
import { SharingInAccordion } from "../../../../../components/Sharing/SharingController";
import { useRouteLoaderData } from "react-router-dom";
import Autodiscover from "./Autodiscover";
import EmailSending from "./EmailSending";
import { getEmailTypeProps } from "../../sendersUtil";
import SignatureSelector from "../../../../../components/Selectors/SignatureSelector";
import { getSignatures } from "../../../../../api/ConfigsApi";
import PoolsSelector from "../../../../../components/Selectors/PoolsSelector";
import CustomDomain from "./CustomDomain";
import {
  useProfileStore,
  useSessionStore,
} from "../../../../../stores/UserStore";
import { getTypeIcon } from "../../../workflows/CreateCampaign/flow/flowSettings";

const EmailSmtp = ({ emailProfile, handleClose, onEdited }) => {
  const lang = useLanguage((s) => s.language);
  const actualPool = useRouteLoaderData("email-pool");
  const [dirty, setDirty] = useState(false);
  const [replyTos, setReplyTos] = useState(emailProfile?.reply_to || []);
  const [pools, setPools] = useState(
    emailProfile?.pools ? emailProfile.pools : actualPool ? [actualPool] : [],
  );
  const [email, setEmail] = useState(emailProfile?.email);
  const [loading, setLoading] = useState(false);

  // Constante pour identifier les comptes Gmail et Outlook
  const isGmailOrOutlook =
    emailProfile?.account_type === "Gmail" ||
    emailProfile?.account_type === "Outlook";

  // --- GESTION DU FOURNISSEUR ---
  const initialProvider = useMemo(() => {
    if (isGmailOrOutlook) {
      return emailProfile?.mailgun_domain ? "mailgun" : "smtp";
    }
    if (emailProfile?.mailgun_domain) return "mailgun";
    if (emailProfile?.smtp_host === "ssl0.ovh.net") return "ovh_perso";
    if (emailProfile?.smtp_host === "pro1.mail.ovh.net") return "ovh_pro";
    return "other";
  }, [emailProfile, isGmailOrOutlook]);

  const [selectedProvider, setSelectedProvider] = useState(initialProvider);
  const isMailgun = selectedProvider === "mailgun";
  const isSmtp = selectedProvider !== "mailgun";

  // Seul l'IMAP peut être ignoré par l'utilisateur
  const [imapRequired, setImapRequired] = useState(
    emailProfile?.imap_host === "" ? false : true,
  );
  const [smtpRequired, setSmtpRequired] = useState(
    isGmailOrOutlook
      ? !!emailProfile?.smtp_host
      : selectedProvider !== "mailgun" || !!emailProfile?.smtp_host,
  );
  const [mailgunRequired, setMailgunRequired] = useState(
    isGmailOrOutlook
      ? !!emailProfile?.mailgun_api_key
      : selectedProvider === "mailgun" || !!emailProfile?.mailgun_domain,
  );

  const sharingForm = useForm({
    defaultValues: {
      sharing:
        emailProfile?.sharing?.map((x) => ({
          ...x,
          sharing_targets: x.sharing_targets.map((sharing_target) => ({
            ...sharing_target,
            option_name: sharing_target.name,
          })),
        })) || [],
    },
  });
  const [sign, setSign] = useState(emailProfile?.email_signatures_id || null);
  const profile = useProfileStore((s) => s.profile);

  const [smtpError, setSmtpError] = useState(false);
  const [imapError, setImapError] = useState(false);

  const shared = emailProfile?.created_by !== profile?.id;

  const parameterValues = useMemo(() => {
    let params = new URLSearchParams(location.search);
    return {
      smtp_host: params.get("smtp_host"),
      smtp_port: params.get("smtp_port"),
      smtp_secure: params.get("smtp_secure"),
      imap_host: params.get("imap_host"),
      imap_password: params.get("imap_password"),
      imap_port: params.get("imap_port"),
      imap_username: params.get("imap_username"),
      imap_secure: params.get("imap_secure"),
    };
  }, []);

  // --- DÉTERMINATION DYNAMIQUE DES VALEURS PAR DÉFAUT (Pour OVH) ---
  const getSmtpHostDefault = () => {
    if (selectedProvider === "ovh_perso") return "ssl0.ovh.net";
    if (selectedProvider === "ovh_pro") return "pro1.mail.ovh.net";
    return emailProfile?.smtp_host || parameterValues?.smtp_host || "";
  };

  const getSmtpPortDefault = () => {
    if (selectedProvider === "ovh_perso") return "465";
    if (selectedProvider === "ovh_pro") return "587";
    return emailProfile?.smtp_port || parameterValues?.smtp_port || "";
  };

  const getImapHostDefault = () => {
    if (selectedProvider === "ovh_perso") return "ssl0.ovh.net";
    if (selectedProvider === "ovh_pro") return "pro1.mail.ovh.net";
    return emailProfile?.imap_host || parameterValues?.imap_host || "";
  };

  const getImapPortDefault = () => {
    if (selectedProvider === "ovh_perso" || selectedProvider === "ovh_pro")
      return "993";
    return emailProfile?.imap_port || parameterValues?.imap_port || "";
  };

  const getSmtpSecureDefault = () => {
    if (selectedProvider === "ovh_perso") return true;
    return (
      emailProfile?.smtp_secure === "ssl" ||
      parameterValues?.smtp_secure === "ssl"
    );
  };

  const getImapSecureDefault = () => {
    if (selectedProvider === "ovh_perso" || selectedProvider === "ovh_pro")
      return true;
    return (
      emailProfile?.imap_secure === "ssl" ||
      parameterValues?.imap_secure === "ssl"
    );
  };

  // --- GESTIONNAIRES D'ÉVÈNEMENTS ---
  const handleErrors = ({ state, details }) => {
    setLoading(false);
    if (
      details ===
        "SMTP Error: Could not connect to SMTP host. Failed to connect to server" ||
      details?.includes("such host")
    ) {
      setSmtpError("server");
      return document.getElementById("smtp_host-input")?.focus();
    }
    if (details?.includes("authenticate") && state?.includes("smtp")) {
      setSmtpError("auth");
      return document.getElementById("smtp_username-input")?.focus();
    }
    if (state?.includes("smtp") || details?.includes("SMTP")) {
      setSmtpError(true);
      document.getElementById("smtp_username-input")?.focus();
    }
    if (
      details ===
        "IMAP Error: Could not connect to IMAP host. Failed to connect to server" ||
      details?.includes("such host")
    ) {
      setImapError("server");
      return document.getElementById("imap_host-input")?.focus();
    }
    if (details?.includes("authenticate") && state?.includes("imap")) {
      setImapError("auth");
      return document.getElementById("imap_username-input")?.focus();
    }
    if (state?.includes("imap") || details?.includes("IMAP")) {
      setImapError(true);
      document.getElementById("imap_username-input")?.focus();
    } else {
      setSmtpError("auth");
      return document.getElementById("smtp_host-input")?.focus();
    }
  };

  const handleDiscovery = (settings) => {
    Object.keys(settings).forEach((key) => {
      const el = document.querySelector(`[name="${key}"]`);
      if (el) el.value = settings[key];
    });
    const smtpSecureEl = document.querySelector(`[name="smtp_secure"]`);
    if (smtpSecureEl) {
      smtpSecureEl.checked = settings.smtp_secure === "ssl" ? true : false;
    }

    const imapSecureEl = document.querySelector(`[name="imap_secure"]`);
    if (imapSecureEl) {
      imapSecureEl.checked = settings.imap_secure === "ssl" ? true : false;
    }

    document.querySelector(`[name="smtp_password"]`)?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);

    setSmtpError(false);
    setImapError(false);

    const sharing = sharingForm
      .getValues()
      .sharing.filter((x) => x.sharing_targets.length > 0)
      .map((share) => ({
        ...share,
        ids_list: share.sharing_targets.map((x) => x.id),
        sharing_targets: undefined,
      }));

    data.smtp_port = isSmtp ? parseInt(data?.smtp_port) : undefined;
    data.smtp_secure = isSmtp
      ? smtpRequired
        ? data?.smtp_secure === "on" || data?.smtp_secure === "ssl"
          ? "ssl"
          : "tls"
        : null
      : null;
    data.smtp_username = isSmtp ? data?.smtp_username || null : null;
    data.smtp_host = isSmtp ? data?.smtp_host || null : null;
    data.smtp_password = isSmtp ? data?.smtp_password || null : null;
    data.smtp_port = isSmtp ? data?.smtp_port || null : null;

    // L'IMAP n'est envoyé que si ce n'est PAS Gmail/Outlook ET qu'il est requis
    data.imap_host =
      imapRequired && !isGmailOrOutlook ? data?.imap_host : undefined;
    data.imap_password =
      imapRequired && !isGmailOrOutlook
        ? data?.imap_password || null
        : undefined;
    data.imap_port =
      imapRequired && !isGmailOrOutlook ? parseInt(data?.imap_port) : undefined;
    data.imap_username =
      imapRequired && !isGmailOrOutlook ? data?.imap_username : undefined;

    data.imap_secure =
      isSmtp || isMailgun
        ? imapRequired
          ? data?.imap_secure === "on" || data?.imap_secure === "ssl"
            ? "ssl"
            : "tls"
          : null
        : null;
    data.connection_test = !!data?.smtp_password || !!data.mailgun_api_key;
    data.email_signatures_id = sign;
    data.read_autoresponse = data.read_autoresponse === "on" ? true : false;
    data.add_sender_as_recipient_in_replies =
      data.add_sender_as_recipient_in_replies === "on" ? true : false;
    data.reply_to = replyTos || [];
    data.pools_ids = pools?.map((x) => x.id) || [];

    let final_data = {
      ...data,
      enable_unsubscribe_link: data.enable_unsubscribe_link ? true : false,
      enable_tracking: data.enable_tracking ? true : false,
      sending_frequency_start: parseInt(data.sending_frequency_start),
      sending_frequency_end: parseInt(data.sending_frequency_end),
      warm_up: {
        day_1: data.day1 ? parseInt(data.day1) : null,
        day_2: data.day2 ? parseInt(data.day2) : null,
        day_3: data.day3 ? parseInt(data.day3) : null,
        day_4: data.day4 ? parseInt(data.day4) : null,
        day_5: data.day5 ? parseInt(data.day5) : null,
        day_6: data.day6 ? parseInt(data.day6) : null,
        day_7: data.day7 ? parseInt(data.day7) : null,
        day_8: data.day8 ? parseInt(data.day8) : null,
        day_9: data.day9 ? parseInt(data.day9) : null,
        day_10: data.day10 ? parseInt(data.day10) : null,
        day_11: data.day11 ? parseInt(data.day11) : null,
        day_12: data.day12 ? parseInt(data.day12) : null,
        day_13: data.day13 ? parseInt(data.day13) : null,
        day_14: data.day14 ? parseInt(data.day14) : null,
        day_15: data.day15 ? parseInt(data.day15) : null,
      },
    };

    setLoading(true);
    const main = emailProfile
      ? () =>
          getEmailTypeProps(emailProfile?.account_type).editFunction({
            id: emailProfile.id,
            data: final_data,
            sharing: shared ? undefined : sharing,
          })
      : () => createSMTP(final_data, sharing);
    main()
      .then((response) => {
        setDirty(false);
        notifications.show({
          message:
            lang.emails_account.row_highlight[
              emailProfile ? "updated" : "created"
            ],
          color: "tertiary.8",
        });
        setTimeout(() => {
          onEdited?.();
        }, 100);
      })
      .catch((err) =>
        handleErrors({
          details: err.response.data?.error_details,
          state: err.response.data?.state_message,
        }),
      )
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEmailBlur = (e) => {
    let v = e.target.value;
    if (v && replyTos.length <= 1 && !emailProfile) {
      setReplyTos([v]);
    }
  };

  const getSmtpError = useCallback(() => {
    if (smtpError === "server") {
      return lang.senders.emails.smtp_server_error;
    }
    if (smtpError === "auth") {
      return lang.senders.emails.smtp_auth_error;
    }
    return lang.senders.emails.smtp_error;
  }, [lang, smtpError]);

  const getImapError = useCallback(() => {
    if (imapError === "server") {
      return lang.senders.emails.imap_server_error;
    }
    if (imapError === "auth") {
      return lang.senders.emails.imap_auth_error;
    }
    return lang.senders.emails.imap_error;
  }, [lang, imapError]);

  useEffect(() => {
    if (emailProfile) {
      getSignatures();
    }
  }, [emailProfile]);

  useNavigationBlocker(dirty);

  // --- SECTIONS DÉCOUPÉES ---
  // On utilise Box avec `key={selectedProvider}` pour forcer le re-rendu et appliquer les bonnes defaultValue.

  const smtpSection = (
    <Box key={`smtp-${selectedProvider}`} mb={"xl"}>
      <Group justify="space-between">
        <Text size="md" mb="sm" fw={600}>
          {lang.emails_account.smtp.step_base.smtp_title}
        </Text>
        {isGmailOrOutlook && (
          <Switch
            label={lang.senders.emails.smtp_use}
            checked={smtpRequired}
            onChange={(e) => setSmtpRequired(e.target.checked)}
            size="md"
          />
        )}
      </Group>

      {selectedProvider === "ovh_pro" && (
        <Text size="sm" c={"primary"} fw={600} mb="sm">
          {lang.senders.emails.smtp_text_ovh_pro}
        </Text>
      )}

      {smtpError && (
        <Text c={"red"} size="sm" mt={-6} fw={500} mb="xs">
          {getSmtpError()}
        </Text>
      )}
      <Group wrap="nowrap">
        <TextInput
          id="smtp_username-input"
          defaultValue={emailProfile?.smtp_username}
          placeholder={lang.emails_account.smtp.step_base.smtp_username}
          name="smtp_username"
          label={lang.emails_account.smtp.step_base.smtp_username}
          required={smtpRequired}
          disabled={!smtpRequired}
          type="text"
          w={"100%"}
          size="md"
        />
        <TextInput
          id="smtp_host-input"
          defaultValue={getSmtpHostDefault()}
          placeholder={lang.emails_account.smtp.step_base.smtp_host}
          name="smtp_host"
          label={lang.emails_account.smtp.step_base.smtp_host}
          type="text"
          w={"100%"}
          required={smtpRequired}
          disabled={!smtpRequired}
          size="md"
        />
      </Group>
      <Group wrap="nowrap" mt="sm">
        <TextInput
          type="number"
          defaultValue={getSmtpPortDefault()}
          placeholder={"465"}
          name="smtp_port"
          label={lang.emails_account.smtp.step_base.smtp_port}
          required={smtpRequired}
          disabled={!smtpRequired}
          w={"100%"}
          size="md"
        />
        <PasswordInput
          defaultValue={emailProfile?.smtp_password}
          placeholder={
            emailProfile
              ? "*****"
              : lang.emails_account.smtp.step_base.smtp_password
          }
          name="smtp_password"
          label={lang.emails_account.smtp.step_base.smtp_password}
          w={"100%"}
          required={emailProfile?.smtp_host ? false : smtpRequired}
          disabled={!smtpRequired}
          autoComplete="new-password"
          size="md"
        />
      </Group>
      <Checkbox
        label={lang.emails_account.smtp.step_base.smtp_ssl}
        defaultChecked={getSmtpSecureDefault()}
        name="smtp_secure"
        disabled={!smtpRequired}
        mt="sm"
        size="md"
      />
    </Box>
  );

  const imapSection = (
    <Box key={`imap-${selectedProvider}`} mb={"xl"}>
      <Group justify="space-between">
        <Text size="md" fw={600}>
          {lang.emails_account.smtp.step_base.imap_title}
        </Text>
        <Switch
          label={lang.senders.emails.imap_ignore}
          checked={!imapRequired}
          onChange={(e) => setImapRequired(!e.target.checked)}
          size="md"
        />
      </Group>
      <Text size="sm" mt={-4} c="dimmed" mb="sm">
        {lang.senders.emails.imap_text}
      </Text>

      {imapError && (
        <Text c={"red"} size="sm" mt={-6} fw={500} mb="xs">
          {getImapError()}
        </Text>
      )}
      <Group wrap="nowrap">
        <TextInput
          id="imap_username-input"
          defaultValue={emailProfile?.imap_username}
          placeholder={lang.emails_account.smtp.step_base.imap_username}
          name="imap_username"
          label={lang.emails_account.smtp.step_base.imap_username}
          required={imapRequired}
          type="text"
          w={"100%"}
          size="md"
        />
        <TextInput
          id="imap_host-input"
          defaultValue={getImapHostDefault()}
          placeholder={lang.emails_account.smtp.step_base.imap_host}
          name="imap_host"
          label={lang.emails_account.smtp.step_base.imap_host}
          w={"100%"}
          required={imapRequired}
          size="md"
        />
      </Group>
      <Group wrap="nowrap" mt="sm">
        <TextInput
          type="number"
          defaultValue={getImapPortDefault()}
          placeholder={"993"}
          name="imap_port"
          label={lang.emails_account.smtp.step_base.imap_port}
          required={imapRequired}
          w={"100%"}
          size="md"
        />
        <PasswordInput
          defaultValue={emailProfile?.imap_password}
          placeholder={
            emailProfile
              ? "*****"
              : lang.emails_account.smtp.step_base.imap_password
          }
          name="imap_password"
          label={lang.emails_account.smtp.step_base.imap_password}
          w={"100%"}
          required={!emailProfile && imapRequired}
          autoComplete="new-password"
          size="md"
        />
      </Group>
      <Checkbox
        label={lang.emails_account.smtp.step_base.imap_ssl}
        defaultChecked={getImapSecureDefault()}
        name="imap_secure"
        mt="sm"
        mb={"md"}
        size="md"
      />
    </Box>
  );

  const mailgunSection = (
    <Box mb={"xl"}>
      <Group justify="space-between">
        <Text size="md" mb="sm" fw={600}>
          Configuration Mailgun
        </Text>
        {isGmailOrOutlook && (
          <Switch
            label={"Utiliser Mailgun"}
            checked={mailgunRequired}
            onChange={(e) => setMailgunRequired(e.target.checked)}
            size="md"
          />
        )}
      </Group>
      {smtpError && (
        <Text c={"red"} size="sm" mt={-6} fw={500} mb="xs">
          Une erreur est survenue lors de la connexion à Mailgun. Veuillez
          vérifier vos identifiants.
        </Text>
      )}
      <Group wrap="nowrap">
        <TextInput
          id="smtp_username-input"
          defaultValue={emailProfile?.mailgun_domain}
          placeholder={"mg.example.com"}
          name="mailgun_domain"
          label={"Domaine Mailgun"}
          required={mailgunRequired}
          disabled={!mailgunRequired}
          type="text"
          w={"100%"}
          size="md"
        />
        <TextInput
          id="smtp_host-input"
          defaultValue={
            emailProfile?.mailgun_region || parameterValues?.mailgun_region
          }
          placeholder={"EU"}
          name="mailgun_region"
          label={"Région Mailgun"}
          type="text"
          w={"100%"}
          required={mailgunRequired}
          disabled={!mailgunRequired}
          size="md"
        />
      </Group>
      <Group wrap="nowrap" mt="sm">
        <TextInput
          defaultValue={
            emailProfile?.mailgun_api_key || parameterValues?.mailgun_api_key
          }
          placeholder={"XXX"}
          name="mailgun_api_key"
          label={"Clé API Mailgun"}
          required={mailgunRequired}
          disabled={!mailgunRequired}
          w={"100%"}
          size="md"
        />
        <TextInput
          defaultValue={
            emailProfile?.mailgun_from_email ||
            parameterValues?.mailgun_from_email
          }
          placeholder={"example@domain.com"}
          name="mailgun_from_email"
          label={"Email d'expédition Mailgun"}
          required={mailgunRequired}
          disabled={!mailgunRequired}
          w={"100%"}
          size="md"
        />
      </Group>
      <Group wrap="nowrap" mt="sm">
        <TextInput
          defaultValue={
            emailProfile?.mailgun_webhook_signing_key ||
            parameterValues?.mailgun_webhook_signing_key
          }
          placeholder={"XXX"}
          name="mailgun_webhook_signing_key"
          label={"Clé de signature du webhook Mailgun"}
          required={mailgunRequired}
          disabled={!mailgunRequired}
          w={"100%"}
          size="md"
        />
      </Group>
    </Box>
  );

  return (
    <Stack
      gap={"xs"}
      id="edit-form"
      flex={"1 1 auto"}
      h={0}
      style={{ overflow: "auto" }}
      mih={emailProfile ? "100%" : "84%"}
      component={"form"}
      w={"100%"}
      onSubmit={handleSubmit}
      onChange={() => setDirty(true)}
      pr={16}
    >
      {emailProfile && (
        <Group justify="center">
          {getTypeIcon(emailProfile?.account_type, false, 22, {
            variant: "subtle",
            color: emailProfile?.account_type === "SMTP" ? "primary" : "pink",
          })}
          <Title order={4} ta={"center"}>
            {lang.emails_account.smtp.edit.dialog.title} : {emailProfile?.name}{" "}
            #{emailProfile?.id}
          </Title>
        </Group>
      )}

      <LoadingOverlay visible={loading} />

      <Group wrap="nowrap">
        <TextInput
          defaultValue={emailProfile?.name}
          placeholder={"My email"}
          name="name"
          label={lang.emails_account.smtp.step_base.name}
          type="text"
          w={"100%"}
          required
          autoFocus
          size="md"
        />

        <TextInput
          defaultValue={emailProfile?.sender}
          placeholder={"John Doe"}
          name="sender"
          label={lang.emails_account.smtp.step_base.sender}
          type="text"
          w={"100%"}
          required
          size="md"
        />
        <TextInput
          id="smtp-email"
          defaultValue={emailProfile?.email}
          placeholder={"email@example.com"}
          onBlur={handleEmailBlur}
          name="email"
          label={lang.emails_account.smtp.step_base.email}
          type="email"
          w={"100%"}
          onChange={(e) => setEmail(e.target.value)}
          required
          size="md"
          rightSectionWidth={110}
          rightSection={
            (emailProfile?.account_type === "SMTP" || !emailProfile) &&
            !["ovh_perso", "ovh_pro", "mailgun"].includes(selectedProvider) && (
              <Autodiscover
                email={email}
                inputId={"smtp-email"}
                onFinish={handleDiscovery}
              />
            )
          }
        />
      </Group>

      <Divider my={12} mx={"30%"} />

      {/* --- SÉLECTEUR DE FOURNISSEUR --- */}
      {isGmailOrOutlook ? (
        <Group w={"100%"}>
          <SegmentedControl
            value={selectedProvider}
            onChange={setSelectedProvider}
            data={[
              { label: "SMTP", value: "smtp" },
              { label: "Mailgun", value: "mailgun" },
            ]}
            mb="md"
            fullWidth
            size="md"
            w={"100%"}
          />
        </Group>
      ) : (
        <Group w={"100%"}>
          <SegmentedControl
            value={selectedProvider}
            onChange={(v) => {
              setSelectedProvider(v);
              setSmtpRequired(v !== "mailgun");
              setMailgunRequired(v === "mailgun");
            }}
            data={[
              { label: "OVH Perso", value: "ovh_perso" },
              { label: "OVH Pro", value: "ovh_pro" },
              { label: "Autre fournisseur", value: "other" },
              { label: "Mailgun", value: "mailgun" },
            ]}
            mb="md"
            fullWidth
            size="md"
            w={"100%"}
          />
        </Group>
      )}

      {/* --- AFFICHAGE CONDITIONNEL DES SECTIONS --- */}
      {!isMailgun && smtpSection}
      {isMailgun && mailgunSection}

      {!isGmailOrOutlook && (
        <>
          <Divider my={12} />
          {imapSection}
          <Divider my={12} mx={"30%"} />
        </>
      )}

      {/* ------------------------------------------- */}

      <Checkbox
        label={lang.new_senders.add_sender_as_recipient_in_replies}
        defaultChecked={emailProfile?.add_sender_as_recipient_in_replies}
        name="add_sender_as_recipient_in_replies"
        mt={4}
        size="md"
      />

      <Checkbox
        label={lang.new_senders.read_autoresponse}
        defaultChecked={emailProfile?.read_autoresponse}
        name="read_autoresponse"
        mt={4}
        size="md"
      />

      <PoolsSelector
        type={"Email"}
        value={pools}
        setValue={setPools}
        size="md"
        mt="md"
      />

      <TagsInput
        name="reply_to"
        label={lang.emails_account.mass_mailing.reply_to.title}
        placeholder={lang.global.enter_key_to_add}
        description={lang.global.enter_key_to_add}
        value={replyTos}
        onChange={setReplyTos}
        mt={"md"}
        mb={"sm"}
        size="md"
      />

      <Accordion variant="contained" mb={"sm"}>
        <Accordion.Item value={"send"}>
          <Accordion.Control
            icon={<IconSignature size={16} />}
            px={"xs"}
            bg={"var(--mantine-color-gray-0)"}
          >
            {lang.emails_account.smtp.step_pools_signs.signature}
          </Accordion.Control>
          <Accordion.Panel>
            <SignatureSelector
              value={sign}
              onChange={(v) => setSign(v?.id || null)}
              size="md"
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {useSessionStore.getState().reseller?.name === "Magileads" && (
        <CustomDomain emailProfile={emailProfile} />
      )}

      <Accordion variant="contained" mb={"sm"} defaultValue={"send"}>
        <Accordion.Item value={"send"}>
          <Accordion.Control
            icon={<IconSend size={16} />}
            px={"xs"}
            bg={"var(--mantine-color-gray-0)"}
          >
            {lang.emails_account.smtp.step_suppl.title}
          </Accordion.Control>
          <Accordion.Panel>
            <EmailSending
              configs={emailProfile}
              mailgun={isMailgun && mailgunRequired}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {!shared && <SharingInAccordion form={sharingForm} readOnly={shared} />}

      <Group
        justify="right"
        mt={"sm"}
        pos={"sticky"}
        bottom={0}
        pt={16}
        style={{ zIndex: 8, background: "#fff" }}
        gap={"xl"}
      >
        <BackButton onClick={handleClose} size="md" />

        {emailProfile?.auth_url && (
          <Anchor target="_self" href={emailProfile.auth_url} size="md">
            <Button variant="light" leftSection={<IconRefreshDot size={18} />}>
              {lang.emails_account.reconnect}
            </Button>
          </Anchor>
        )}

        <Button
          type="submit"
          color="primary"
          size="md"
          leftSection={
            emailProfile ? (
              <IconDeviceFloppy size={18} />
            ) : (
              <IconPlus size={18} />
            )
          }
          loading={loading}
        >
          {emailProfile
            ? lang.model_emails.edit.dialog.submit_button
            : lang.emails_account.create.dialog.submit_button}
        </Button>
      </Group>
    </Stack>
  );
};

export default EmailSmtp;
